import { z } from "zod";
import { PROGRESSION_STATUSES } from "@/features/recruitment/recruitment.taxonomy";

/**
 * URL search params for `/$workspaceSlug/recruitment/openings/$openingId`.
 *
 * HRMS-REC-UX P4.5. A job is the object you open, and everything about it
 * lives behind a chip on the job rather than a tab at the top of the module.
 * Which chip is open is URL state, not `useState` (S3): "the offers on
 * Backend engineer" is a link a recruiter sends a hiring manager, the back
 * button undoes a chip change instead of leaving the job, and a refresh lands
 * where the reader was.
 *
 * Every key here is PREFIXED, and that is deliberate rather than decorative.
 * The hiring home carries `tab`, `opening`, `candStage`, `openingQ`,
 * `openingStatus`, `reqQ`, `reqStatus` and `planQ`. A job link is pasted
 * beside a home link constantly, and a bare `status` or `tab` on this route
 * would read as the home's and silently mean something else. `jobChip` names
 * the strip; `int*` and `off*` name whose filter each one is.
 *
 * The enums are spelled out here rather than imported from the interview and
 * offer schema modules, the same bundle decision `interviews.route.search.ts`
 * documents: `router.tsx` imports this module EAGERLY for `validateSearch`,
 * so anything it reaches lands in the initial chunk (P1). Nine literals cost
 * nothing; the two contracts they came from would not.
 */
const INTERVIEW_STATUSES = ["pending", "under_review", "cleared", "rejected"] as const;
const OFFER_STATUSES = ["awaiting_response", "accepted", "rejected", "withdrawn"] as const;

/// Appended, never inserted, for the reason the home's tab enum gives: a chip
/// that shifts the ones beside it breaks links people have already saved.
export const JOB_CHIPS = ["pipeline", "interviews", "offers", "letters", "details"] as const;

export const recruitmentJobSearchSchema = z
  .object({
    jobChip: z.enum(JOB_CHIPS).optional(),
    /// The pipeline chip's stage filter, owned by this route.
    ///
    /// P4.6: `ApplicantBoard` used to read this straight off the old
    /// recruitment route with getRouteApi, which threw the moment the board
    /// also mounted here. The board takes it as a prop now, so the key is
    /// spelled per route; `jobStage` rather than the home's `candStage`
    /// because they are different lists and a shared name would let one
    /// route's link narrow the other's board.
    ///
    /// Only progression statuses: `hold` and `rejected` are not stages, and a
    /// hand-typed one is refused rather than dropped, so a link cannot put a
    /// parked candidate back inside the ramp.
    jobStage: z
      .union([z.enum(PROGRESSION_STATUSES), z.array(z.enum(PROGRESSION_STATUSES))])
      .transform((v) => {
        const list = Array.isArray(v) ? v : [v];
        const seen = [...new Set(list)];
        return seen.length === 0 ? undefined : seen;
      })
      .optional(),
    /// Interviews chip: the shore's own status filter plus its `YYYY-MM-DD`
    /// date bounds, both inclusive.
    intStatus: z.enum(INTERVIEW_STATUSES).optional(),
    intFrom: z.string().optional(),
    intTo: z.string().optional(),
    /// Offers chip: the shore's own status filter.
    offStatus: z.enum(OFFER_STATUSES).optional(),
  })
  .strict();

export type RecruitmentJobSearch = z.infer<typeof recruitmentJobSearchSchema>;
export type JobChip = (typeof JOB_CHIPS)[number];

/// Which chips this reader holds the key for. Pipeline and Details are absent
/// on purpose: the route gate is the pipeline key, so anybody past it can read
/// both, and a flag that is always true is a flag that will one day be wrong.
export interface JobChipAccess {
  interviews: boolean;
  offers: boolean;
  letters: boolean;
}

/**
 * Which chip the URL means, for a given viewer.
 *
 * A hand-typed chip the reader cannot open resolves to Pipeline, NOT to the
 * access card. The two are not interchangeable and the difference matters:
 * the access card is the answer to "may you open this page", and the route
 * body already gave that answer before this function runs. Someone who is
 * past that gate but holds no offers key is not being refused the job, only
 * one section of it, and replacing the whole job with "Admin only" would tell
 * them something false about the page they are on.
 *
 * It is also the only coherent surface. The strip renders the chips a reader
 * holds, so honouring `?jobChip=offers` for somebody without the key would
 * select a chip that is not drawn above the body it selected. That is the
 * same call `resolveRecruitmentTab` makes for a governance tab the workspace
 * has switched off, and it is non-destructive for the same reason: the key
 * stays in the URL, so the moment the reader is granted the permission that
 * same link opens the chip it names.
 */
export function resolveJobChip(search: RecruitmentJobSearch, access: JobChipAccess): JobChip {
  const requested = search.jobChip ?? "pipeline";
  if (requested === "interviews" && !access.interviews) return "pipeline";
  if (requested === "offers" && !access.offers) return "pipeline";
  if (requested === "letters" && !access.letters) return "pipeline";
  return requested;
}
